import React, { Suspense, lazy, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from './firebase';
import { precalentarProductos } from './datos/almacenes';
import Login from './pages/Login';
import CampanaNotificaciones from './components/CampanaNotificaciones';
import PedirContrasena from './components/PedirContrasena';
import useCierreDiario from './hooks/useCierreDiario';

// Carga diferida: cada pantalla se descarga recien cuando se entra a esa
// ruta, en vez de bajar la app entera (Stock, POS, Clientes, ML, etc.) de
// una sola vez al abrir el login. Esto es lo que hacia que la app tardara
// cada vez mas en cargar a medida que se sumaban pantallas nuevas.
// Si la pestaña quedó abierta desde antes de publicar una versión nueva, los
// archivos viejos de cada pantalla ya no existen y la carga falla. En ese
// caso se recarga la página una vez para traer la versión nueva (pedido de
// Mica, 2026-10-07: no veía cambios recién publicados).
function lazyConRecarga(importar) {
  return lazy(() => importar().catch((err) => {
    const CLAVE = 'recargado_por_version_nueva';
    let yaRecargo = false;
    try { yaRecargo = sessionStorage.getItem(CLAVE) === '1'; sessionStorage.setItem(CLAVE, '1'); } catch (_) { /* sin storage */ }
    if (!yaRecargo) {
      window.location.reload();
      return new Promise(() => {});
    }
    throw err;
  }).then((mod) => {
    try { sessionStorage.removeItem('recargado_por_version_nueva'); } catch (_) { /* sin storage */ }
    return mod;
  }));
}

const Dashboard = lazyConRecarga(() => import('./pages/Dashboard'));
const Admin = lazyConRecarga(() => import('./pages/Admin'));
const CambiarPassword = lazyConRecarga(() => import('./pages/CambiarPassword'));
const Stock = lazyConRecarga(() => import('./pages/Stock'));
const Depositos = lazyConRecarga(() => import('./pages/Depositos'));
const RecepcionStock = lazyConRecarga(() => import('./pages/RecepcionStock'));
const ImportarProductos = lazyConRecarga(() => import('./pages/ImportarProductos'));
const ConfigurarMargenes = lazyConRecarga(() => import('./pages/ConfigurarMargenes'));
const MargenesPorProducto = lazyConRecarga(() => import('./pages/MargenesPorProducto'));
const ConfigurarPuntos = lazyConRecarga(() => import('./pages/ConfigurarPuntos'));
const PosiblesDuplicadosTN = lazyConRecarga(() => import('./pages/PosiblesDuplicadosTN'));
const SugerenciasFichaML = lazyConRecarga(() => import('./pages/SugerenciasFichaML'));
const ActualizarPrecios = lazyConRecarga(() => import('./pages/ActualizarPrecios'));
const RevisionPrecios = lazyConRecarga(() => import('./pages/RevisionPrecios'));
const SyncErrores = lazyConRecarga(() => import('./pages/SyncErrores'));
const DevolucionesML = lazyConRecarga(() => import('./pages/DevolucionesML'));
const DetalleProducto = lazyConRecarga(() => import('./pages/DetalleProducto'));
const AlertasStock = lazyConRecarga(() => import('./pages/AlertasStock'));
const ConfigurarComisionML = lazyConRecarga(() => import('./pages/ConfigurarComisionML'));
const CostosOperativos = lazyConRecarga(() => import('./pages/CostosOperativos'));
const DashboardRentabilidad = lazyConRecarga(() => import('./pages/DashboardRentabilidad'));
const Kits = lazyConRecarga(() => import('./pages/Kits'));
const Clientes = lazyConRecarga(() => import('./pages/Clientes'));
const Pos = lazyConRecarga(() => import('./pages/Pos'));
const HistorialCaja = lazyConRecarga(() => import('./pages/HistorialCaja'));
const Envios = lazyConRecarga(() => import('./pages/Envios'));
const MercadoLibre = lazyConRecarga(() => import('./pages/MercadoLibre'));
const CompletarPublicacionesML = lazyConRecarga(() => import('./pages/CompletarPublicacionesML'));
const PublicadorML = lazyConRecarga(() => import('./pages/PublicadorML'));
const CorregirPreciosML = lazyConRecarga(() => import('./pages/CorregirPreciosML'));
const Proveedores = lazyConRecarga(() => import('./pages/Proveedores'));
const PedidosProveedor = lazyConRecarga(() => import('./pages/PedidosProveedor'));
const Ventas = lazyConRecarga(() => import('./pages/Ventas'));
const MisPuntos = lazyConRecarga(() => import('./pages/MisPuntos'));
const Marketing = lazyConRecarga(() => import('./pages/Marketing'));
const Reportes = lazyConRecarga(() => import('./pages/Reportes'));
const Gastos = lazyConRecarga(() => import('./pages/Gastos'));
const ConsultarPrecios = lazyConRecarga(() => import('./pages/ConsultarPrecios'));
const CumpleFecha = lazyConRecarga(() => import('./pages/CumpleFecha'));
const NoRecibirEmails = lazyConRecarga(() => import('./pages/CumpleFecha').then((m) => ({ default: m.NoRecibirEmails })));
const RestablecerPassword = lazyConRecarga(() => import('./pages/RestablecerPassword'));

const ES_DOMINIO_PUNTOS = window.location.hostname.startsWith('comunidad.');

function CargandoPantalla() {
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: '#f5f5f5', fontFamily: 'Arial, sans-serif', color: '#999',
    }}>
      Cargando...
    </div>
  );
}

// Usuario recién creado: la contraseña inicial es su DNI (fácil de adivinar),
// así que hasta que la cambie solo puede usar /cambiar-password.
function ForzarCambioPassword({ activo }) {
  const { pathname } = useLocation();
  if (!activo || pathname === '/cambiar-password' || pathname === '/mis-puntos') return null;
  return <Navigate to="/cambiar-password" replace />;
}

function RutaPrivada({ usuario, cargando, rol, soloAdmin, modulo, modulosPermitidos, children }) {
  if (cargando) return null;
  if (!usuario) return <Navigate to="/" replace />;
  if (soloAdmin && rol !== 'admin') return <Navigate to="/dashboard" replace />;
  if (modulo && rol !== 'admin' && !modulosPermitidos?.includes(modulo)) return <Navigate to="/dashboard" replace />;
  return children;
}

function App() {
  const [usuario, setUsuario] = useState(null);
  const [perfil, setPerfil] = useState(null);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Una cuenta sin ficha en "usuarios" (no la creó un admin) o dada de
        // baja no entra: igual las reglas de Firestore le negarían todo.
        const snap = await getDoc(doc(db, 'usuarios', user.uid)).catch(() => null);
        // (Si la lectura falló por red, no se la echa: queda como antes.)
        if (snap && (!snap.exists() || snap.data().activo === false)) {
          await signOut(auth);
          return;
        }
        setUsuario(user);
        setPerfil(snap?.exists() ? snap.data() : null);
      } else {
        setUsuario(null);
        setPerfil(null);
      }
      setCargando(false);
    });
    return unsubscribe;
  }, []);

  // Ya no se cierra la sesión sola a los 30 minutos (pedido de Mica,
  // 2026-10-16: "no quiero que se cierre la sesión a cada rato"). Las
  // pantallas con plata siguen pidiendo la contraseña (PedirContrasena).

  // Pero sí se cierra todos los días a las 23:59 (pedido de Mica, 2026-10-26).
  useCierreDiario(ES_DOMINIO_PUNTOS ? null : usuario);

  // Con la sesión abierta, el catálogo se empieza a traer solo un momento
  // después de entrar (sin frenar la primera pantalla), así Stock, Consultar
  // precios o el POS abren con los productos ya listos.
  useEffect(() => {
    if (!usuario || !perfil) return undefined;
    const t = setTimeout(precalentarProductos, 1500);
    return () => clearTimeout(t);
  }, [usuario, perfil]);

  const rol = perfil?.rol;
  const modulosPermitidos = perfil?.modulos;

  return (
    <Router>
      {usuario && <CampanaNotificaciones perfil={perfil} />}
      <ForzarCambioPassword activo={!!usuario && perfil?.debe_cambiar_password === true && !ES_DOMINIO_PUNTOS} />
      <Suspense fallback={<CargandoPantalla />}>
        <Routes>
          {/* Si ya hay sesión abierta, "/" lleva directo al panel: antes
              mostraba el login aunque la sesión siguiera viva, y al actualizar
              la página parecía que se había cerrado (Mica, 2026-10-08). */}
          <Route
            path="/"
            element={ES_DOMINIO_PUNTOS ? <MisPuntos /> : (
              cargando ? <CargandoPantalla /> : usuario ? <Navigate to="/dashboard" replace /> : <Login />
            )}
          />
          <Route path="/mis-puntos" element={<MisPuntos />} />
          {/* Páginas públicas de los links de los emails (sin login). */}
          <Route path="/restablecer" element={<RestablecerPassword />} />
          <Route path="/cumple" element={<CumpleFecha />} />
          <Route path="/no-recibir-emails" element={<NoRecibirEmails />} />
          <Route
            path="/dashboard"
            element={
              <RutaPrivada usuario={usuario} cargando={cargando} rol={rol}>
                <Dashboard usuario={usuario} perfil={perfil} />
              </RutaPrivada>
            }
          />
          <Route
            path="/admin"
            element={
              <RutaPrivada usuario={usuario} cargando={cargando} rol={rol} soloAdmin>
                <Admin />
              </RutaPrivada>
            }
          />
          <Route
            path="/cambiar-password"
            element={
              <RutaPrivada usuario={usuario} cargando={cargando} rol={rol}>
                <CambiarPassword
                  obligatorio={perfil?.debe_cambiar_password === true}
                  onCambiada={() => setPerfil((p) => ({ ...p, debe_cambiar_password: false }))}
                />
              </RutaPrivada>
            }
          />
          <Route
            path="/stock"
            element={
              <RutaPrivada usuario={usuario} cargando={cargando} rol={rol} modulo="stock" modulosPermitidos={modulosPermitidos}>
                <Stock />
              </RutaPrivada>
            }
          />
          <Route
            path="/stock/recepcion"
            element={
              <RutaPrivada usuario={usuario} cargando={cargando} rol={rol} modulo="stock" modulosPermitidos={modulosPermitidos}>
                <RecepcionStock perfil={perfil} />
              </RutaPrivada>
            }
          />
          <Route
            path="/stock/producto/:sku"
            element={
              <RutaPrivada usuario={usuario} cargando={cargando} rol={rol} modulo="stock" modulosPermitidos={modulosPermitidos}>
                <DetalleProducto perfil={perfil} />
              </RutaPrivada>
            }
          />
          <Route
            path="/stock/sync-errores"
            element={
              <RutaPrivada usuario={usuario} cargando={cargando} rol={rol} modulo="stock" modulosPermitidos={modulosPermitidos}>
                <SyncErrores />
              </RutaPrivada>
            }
          />
          <Route
            path="/stock/devoluciones-ml"
            element={
              <RutaPrivada usuario={usuario} cargando={cargando} rol={rol} modulo="devoluciones" modulosPermitidos={modulosPermitidos}>
                <DevolucionesML perfil={perfil} />
              </RutaPrivada>
            }
          />
          <Route
            path="/stock/alertas"
            element={
              <RutaPrivada usuario={usuario} cargando={cargando} rol={rol} soloAdmin>
                <AlertasStock />
              </RutaPrivada>
            }
          />
          <Route
            path="/stock/depositos"
            element={
              <RutaPrivada usuario={usuario} cargando={cargando} rol={rol}>
                {/* Lo ve y lo pide todo el personal; mover, solo quien aprueba (Mica, 2026-10-24). */}
                <Depositos perfil={perfil} />
              </RutaPrivada>
            }
          />
          <Route
            path="/stock/posibles-duplicados-tn"
            element={
              <RutaPrivada usuario={usuario} cargando={cargando} rol={rol} modulo="stock" modulosPermitidos={modulosPermitidos}>
                <PosiblesDuplicadosTN />
              </RutaPrivada>
            }
          />
          <Route
            path="/admin/importar-productos"
            element={
              <RutaPrivada usuario={usuario} cargando={cargando} rol={rol} soloAdmin>
                <ImportarProductos />
              </RutaPrivada>
            }
          />
          <Route
            path="/admin/margenes"
            element={
              <RutaPrivada usuario={usuario} cargando={cargando} rol={rol} soloAdmin>
                <ConfigurarMargenes />
              </RutaPrivada>
            }
          />
          <Route
            path="/admin/margenes-producto"
            element={
              <RutaPrivada usuario={usuario} cargando={cargando} rol={rol} soloAdmin>
                <MargenesPorProducto />
              </RutaPrivada>
            }
          />
          <Route
            path="/admin/puntos"
            element={
              <RutaPrivada usuario={usuario} cargando={cargando} rol={rol} soloAdmin>
                <ConfigurarPuntos />
              </RutaPrivada>
            }
          />
          <Route
            path="/mercado-libre/fichas"
            element={
              <RutaPrivada usuario={usuario} cargando={cargando} rol={rol} modulo="mercado_libre" modulosPermitidos={modulosPermitidos}>
                <SugerenciasFichaML />
              </RutaPrivada>
            }
          />
          <Route
            path="/admin/precios-proveedores"
            element={
              <RutaPrivada usuario={usuario} cargando={cargando} rol={rol} soloAdmin>
                <ActualizarPrecios />
              </RutaPrivada>
            }
          />
          <Route
            path="/admin/revision-precios"
            element={
              <RutaPrivada usuario={usuario} cargando={cargando} rol={rol} soloAdmin>
                <RevisionPrecios />
              </RutaPrivada>
            }
          />
          <Route
            path="/admin/comision-mercadolibre"
            element={
              <RutaPrivada usuario={usuario} cargando={cargando} rol={rol} soloAdmin>
                <ConfigurarComisionML />
              </RutaPrivada>
            }
          />
          <Route
            path="/admin/costos-operativos"
            element={
              <RutaPrivada usuario={usuario} cargando={cargando} rol={rol} soloAdmin>
                <CostosOperativos />
              </RutaPrivada>
            }
          />
          <Route
            path="/admin/rentabilidad"
            element={
              <RutaPrivada usuario={usuario} cargando={cargando} rol={rol} soloAdmin>
                <PedirContrasena titulo="Rentabilidad" seccion="Rentabilidad">
                  <DashboardRentabilidad />
                </PedirContrasena>
              </RutaPrivada>
            }
          />
          <Route
            path="/stock/kits"
            element={
              <RutaPrivada usuario={usuario} cargando={cargando} rol={rol} modulo="kits" modulosPermitidos={modulosPermitidos}>
                <Kits />
              </RutaPrivada>
            }
          />
          <Route
            path="/clientes"
            element={
              <RutaPrivada usuario={usuario} cargando={cargando} rol={rol} modulo="puntos" modulosPermitidos={modulosPermitidos}>
                <Clientes perfil={perfil} />
              </RutaPrivada>
            }
          />
          <Route
            path="/pos"
            element={
              <RutaPrivada usuario={usuario} cargando={cargando} rol={rol} soloAdmin>
                <Pos />
              </RutaPrivada>
            }
          />
          <Route
            path="/pos/historial"
            element={
              <RutaPrivada usuario={usuario} cargando={cargando} rol={rol} soloAdmin>
                <HistorialCaja />
              </RutaPrivada>
            }
          />
          <Route
            path="/envios"
            element={
              <RutaPrivada usuario={usuario} cargando={cargando} rol={rol} modulo="envios" modulosPermitidos={modulosPermitidos}>
                <Envios perfil={perfil} />
              </RutaPrivada>
            }
          />
          <Route
            path="/mercado-libre"
            element={
              <RutaPrivada usuario={usuario} cargando={cargando} rol={rol} modulo="mercado_libre" modulosPermitidos={modulosPermitidos}>
                <MercadoLibre perfil={perfil} />
              </RutaPrivada>
            }
          />
          <Route
            path="/mercado-libre/publicador"
            element={
              <RutaPrivada usuario={usuario} cargando={cargando} rol={rol} soloAdmin>
                <PublicadorML />
              </RutaPrivada>
            }
          />
          <Route
            path="/mercado-libre/completar"
            element={
              <RutaPrivada usuario={usuario} cargando={cargando} rol={rol} modulo="mercado_libre" modulosPermitidos={modulosPermitidos}>
                <CompletarPublicacionesML />
              </RutaPrivada>
            }
          />
          <Route
            path="/mercado-libre/corregir-precios"
            element={
              <RutaPrivada usuario={usuario} cargando={cargando} rol={rol} modulo="costos" modulosPermitidos={modulosPermitidos}>
                <CorregirPreciosML />
              </RutaPrivada>
            }
          />
          <Route
            path="/proveedores"
            element={
              <RutaPrivada usuario={usuario} cargando={cargando} rol={rol} soloAdmin>
                <PedirContrasena titulo="Proveedores" seccion="Proveedores">
                  <Proveedores />
                </PedirContrasena>
              </RutaPrivada>
            }
          />
          <Route
            path="/consultar-precios"
            element={
              <RutaPrivada usuario={usuario} cargando={cargando} rol={rol}>
                <ConsultarPrecios perfil={perfil} />
              </RutaPrivada>
            }
          />
          <Route
            path="/gastos"
            element={
              <RutaPrivada usuario={usuario} cargando={cargando} rol={rol} soloAdmin>
                <PedirContrasena titulo="Gastos" seccion="Gastos">
                  <Gastos />
                </PedirContrasena>
              </RutaPrivada>
            }
          />
          <Route
            path="/reportes"
            element={
              <RutaPrivada usuario={usuario} cargando={cargando} rol={rol} soloAdmin>
                {/* La contraseña la pide cada solapa por separado (ver Reportes.js). */}
                <Reportes />
              </RutaPrivada>
            }
          />
          <Route
            path="/marketing"
            element={
              <RutaPrivada usuario={usuario} cargando={cargando} rol={rol} soloAdmin>
                <Marketing />
              </RutaPrivada>
            }
          />
          <Route
            path="/proveedores/pedidos"
            element={
              <RutaPrivada usuario={usuario} cargando={cargando} rol={rol} modulo="pedidos_proveedor" modulosPermitidos={modulosPermitidos}>
                <PedidosProveedor perfil={perfil} />
              </RutaPrivada>
            }
          />
          <Route
            path="/ventas"
            element={
              <RutaPrivada usuario={usuario} cargando={cargando} rol={rol} soloAdmin>
                <PedirContrasena titulo="Ventas" seccion="Ventas">
                  <Ventas />
                </PedirContrasena>
              </RutaPrivada>
            }
          />
        </Routes>
      </Suspense>
    </Router>
  );
}

export default App;
